import { Client } from "@/Common/Api/Client";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { PortalStatusIcon } from "@/Common/Components/Portal/PortalStatusIcon";
import { CommonTableHeaderComponent } from "@/Common/Components/Tables/CommonTableHeaderComponent";
import { MyleTable } from "@/Common/Components/Tables/MyleTable";
import { MyleTableColumn, MyleTableColumnOptions } from "@/Common/Components/Tables/MyleTableColumn";
import { MyleTableColumnOrderControlv2 } from "@/Common/Components/Tables/MyleTableColumnOrderControlv2";
import { SimplePagerControl } from "@/Common/Components/Tables/SimplePagerControl";
import { NameHelper } from "@/Common/Helpers/NameHelper";
import { PortalHelper } from "@/Common/Helpers/PortalHelper";
import { PhoneNumber } from "@/Common/Models/Contacts/PhoneNumber";
import { PortalSyncStatus } from "@/Common/Models/Portal/Enums/PortalSyncStatus";
import { ITableRenderOptions } from "@/Common/Models/UI/Tables/ITableRenderOptions";
import { MyleTableTarget } from "@/Common/Models/UI/Tables/MyleTableTarget";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { SendToPortalPopup } from "@/Portal/Components/SendToPortalPopup";
import { sharedItemProviderFactory } from "@/Portal/Services/SharedItemProviderFactory";
import { PortalSyncHistoryTableItem } from "@/Portfolio/Models/PortalSyncHistory";
import { IPortalSyncModel } from "@/Portfolio/Models/UI/IPortalSyncModel";
import { IPortalSyncSettingsModel } from "@/Portfolio/Models/UI/IPortalSyncSettingsModel";
import { SendFromPortfolioStrategy } from "@/Portfolio/Services/Portal/Strategies/SendFromPortfolioStrategy";
import { PortalSyncSettingsContainer } from "./PortalSyncSettingsContainer";

interface IPortalSyncProps {
    model: IPortalSyncModel;
}

abstract class PortalSyncBaseComponent extends BaseReactComponent<IPortalSyncProps, null> {
    protected abstract getSaveOptionKey(): string;
    protected abstract getMyleTableColumnsOptions(): MyleTableColumnOptions[];
    protected abstract showStatusFilters(): boolean;

    public componentDidMount(): void {
        $(window).trigger("resize");

        sessionStore.dispatch(LocalOperations.Portfolio.PortalSync_Reload);
    }

    public componentDidUpdate(prevProps: IPortalSyncProps): void {
        if (this.props.model.get("GetErrorOnly") !== prevProps.model.get("GetErrorOnly"))
            sessionStore.dispatch(LocalOperations.Portfolio.PortalSync_Reload);
    }

    public render(): JSX.Element {
        const syncSettings: IPortalSyncSettingsModel = this.props.model.get("Settings");

        const saveOptionKey = this.getSaveOptionKey();
        const columnsOptions = cacheStore.Helper.loadTableColumnOptionsFromSettings(saveOptionKey, this.getMyleTableColumnsOptions());

        const columnConfigurationModel = cacheStore.Helper.castColumnConfigurationToMyleTableColumnOptions(columnsOptions.toArray());
        const tableColumns = this.getTableColumns(columnsOptions);
        const tableRenderOptions = this.loadTableRenderOptions(columnsOptions);

        return (
            <div className="workspace">
                <CommonTableHeaderComponent
                    title={cacheStore.GetString(this.props.model.get("GetErrorOnly") ? "ItemsInErrorLabel" : "PortalPendingItemsLabel")}
                    searchText={syncSettings.get("SearchText")}
                    searchPlaceholder={cacheStore.GetString("SearchLabel")}
                    onSearch={text => sessionStore.dispatch(LocalOperations.Portfolio.PortalSync_SearchBoxTextChanged, { value: text })}
                    onRefreshClick={() => sessionStore.dispatch(LocalOperations.Portfolio.PortalSync_Reload)}
                    onSettingsClick={() => sessionStore.dispatch(LocalOperations.Portfolio.PortalSync_SearchSettings_Open)}
                />
                <div id="posref" className="tb ws-scrollable">
                    {this.props.model.get("SearchFilterPopupOpen") && (
                        <PortalSyncSettingsContainer
                            className="filter-component"
                            model={syncSettings}
                            columnOrderPopupOpen={this.props.model.get("ColumnOrderPopupOpen")}
                            showStatuses={this.showStatusFilters()}
                        />
                    )}

                    <div className="myle-doc-tb main-content table-wrapper">
                        <MyleTable
                            TableRenderOptions={tableRenderOptions}
                            TableId="PortalSyncHistory_main_tb"
                            Columns={tableColumns}
                            DataSource={this.props.model.get("TableData")}
                            ColumnsPreferencesChangedOperation={ServerOperations.Server.Myle_UpdateUserSettings}
                            RowClickOperation={LocalOperations.Portfolio.PortalSync_RowClick}
                            SaveOptionskeyTarget={saveOptionKey}
                            ColumnSortTarget={MyleTableTarget.Myle_Portfolio_PortalSync_ColumnSortClick}
                        />
                    </div>
                </div>
                <div className="ws-footer table-pager-footer">
                    <div className="row">
                        <div className="col-lg-10 col-md-9 col-sm-8">
                            <SimplePagerControl
                                PageSize={syncSettings.get("PageSize")}
                                TotalRows={this.props.model.get("TotalRows")}
                                CurrentRowOffset={this.props.model.get("CurrentRowOffset")}
                                EventToDispatchOnPageChange={LocalOperations.Portfolio.PortalSync_CurrentPageChanged}
                            />
                        </div>
                    </div>
                </div>
                {this.props.model.get("ColumnOrderPopupOpen") && (
                    <MyleTableColumnOrderControlv2
                        TableColumns={columnConfigurationModel}
                        SaveOptionskeyTarget={saveOptionKey}
                        ContainerId="columnSettingsPopup"
                        CloseColumOrder={LocalOperations.Portfolio.PortalSync_ColumnOrder_PopupClose}
                    />
                )}
                {this.props.model.get("SyncToPortalPopupOpen") && (
                    <SendToPortalPopup
                        model={this.props.model.get("SyncToPortalModel")}
                        strategy={new SendFromPortfolioStrategy(this.props.model.get("SyncToPortalModel"))}
                        largePreview
                    />
                )}
            </div>
        );
    }

    private getTableColumns(columnsOptions: Immutable.List<MyleTableColumnOptions>): Immutable.List<MyleTableColumn> {
        return MyleTableColumn.buildList<PortalSyncHistoryTableItem>(
            columnsOptions,
            column => {
                switch (column.FieldName) {
                    case "PatientName":
                        return {
                            renderCell: item => (
                                <div>
                                    <a onClick={() => this.onPatientClick(item)}>
                                        {NameHelper.formatFullNameForHasName(item.Model.Patient)}
                                    </a>
                                </div>
                            )
                        };

                    case "PatientPhoneNumbers":
                        return {
                            renderCell: item => {
                                const firstTwoByPriority = PhoneNumber.GetPhonesByPriority(item.Model.PhoneNumbers)
                                    .filter((_, i) => i < 2);

                                return (
                                    <div>
                                        {firstTwoByPriority.map((p, i) => <div key={`phoneNumber_${item.Model.Patient.Id}_${i}`} className="full-width">{p.format()}</div>)}
                                    </div>
                                );
                            }
                        };

                    case "SyncItemName":
                        return {
                            renderCell: item => {
                                const subjectText = item.Model.Subject || " - ";
                                if (!IsNullOrWhiteSpace(item.Model.MessageText)) {
                                    return (
                                        <div>
                                            <p>{cacheStore.GetString("MessageLabel")}: {subjectText}</p>
                                            <p style={{ fontStyle: "italic" }}>{item.Model.MessageText}</p>
                                        </div>
                                    );
                                }

                                const subjectLabel = sharedItemProviderFactory.get(item.Model.SharedItemType).getSharedItemTypeLabel();

                                return (
                                    <div>
                                        <p>{subjectLabel}: {subjectText}</p>
                                    </div>
                                );
                            }
                        };

                    case "SyncDate":
                        return {
                            renderCell: item => {
                                const localDate = item.Model.SyncDate.local();
                                return <div>{localDate.format("YYYY/MM/DD") + " " + cacheStore.GetString("AtLabel") + " " + localDate.format("HH:mm:ss A")}</div>;
                            }
                        };

                    case "SyncUser":
                        return {
                            renderCell: item => (
                                <div>
                                    {item.Model.User.Prefix && cacheStore.GetPrefixIndividualLookups()[item.Model.User.Prefix]}
                                    {item.Model.User.LastName}, {item.Model.User.FirstName}
                                </div>
                            )
                        };

                    case "PortalStatus":
                        return {
                            renderCell: item => {
                                const buttonProps = PortalHelper.getPortalButtonPropsForSyncStatus(
                                    this.props.model.get("GetErrorOnly") ? PortalSyncStatus.Error : PortalSyncStatus.Pending,
                                    item.Model.CurrentIncludeLinkedItem,
                                    item.Model.HasBeenModifiedSinceLastShared
                                );

                                return <PortalStatusIcon {...buttonProps} />;
                            }
                        };
                }
            },
            false
        );
    }

    private loadTableRenderOptions(ColumnOptions: Immutable.List<MyleTableColumnOptions>): ITableRenderOptions {
        // Get The Render Options
        return Immutable.fromJS({
            MinColWidth: 60,
            MinTableWidth: 980,
            Cols: ColumnOptions.filter(p => !p.IsHidden).map(p => p.WidthPercentage),
            FirstColumnNoDrag: false,
            LastColumnNoDrag: true,
            Autoresize: true,
            OnColumnWidthChange: null
        });
    }

    private async onPatientClick(item: PortalSyncHistoryTableItem): Promise<void> {
        const response = await Client.postAsync<{ EncounterId: string | null }>(
            ServerOperations.Server.Myle_GetEncounterIdFrom_SharedItem,
            {
                SharedItemId: item.Model.SharedItemId,
                SharedItemType: item.Model.SharedItemType
            }
        );

        sessionStore.dispatch(LocalOperations.Portfolio.PortalSync_PatientClick, {
            item,
            encounterId: response.data?.EncounterId
        });
    }
}

export { PortalSyncBaseComponent };
